import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Check, ListFilter } from 'lucide-react'

export interface CategoryOption {
  name: string
  count: number
}

interface CategoryFilterProps {
  categories: CategoryOption[]
  /** Empty means no filter, which shows everything. */
  value: string[]
  onChange: (value: string[]) => void
}

const toTestId = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-')

// neo's Popover, not the `FacetedFilter` block: that block hardcodes a dashed labelled trigger,
// and reshaping it would change every table filter in the app.
export const CategoryFilter = ({ categories, value, onChange }: CategoryFilterProps) => {
  // One category is a label, not a filter.
  if (categories.length < 2) return null

  const toggle = (name: string) =>
    onChange(value.includes(name) ? value.filter((item) => item !== name) : [...value, name])

  const isFiltered = value.length > 0

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          // With the popover closed, the trigger is the only sign that a filter is on.
          className={cn('relative flex-none', isFiltered && 'border-transparent bg-bg3')}
          aria-label="Filter by category"
          data-testid="button-category-filter"
        >
          <ListFilter className="h-4 w-4" />
          {isFiltered && (
            <span
              className="-right-1.5 -top-1.5 absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-primary-foreground text-xxs-600"
              data-testid="text-category-filter-count"
            >
              {value.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/*
        Both `disablePortal` and the wheel stop are needed inside the modal Sheet: its
        react-remove-scroll `document` wheel listener cancels events from outside its subtree,
        which kills trackpad scrolling in a portalled popover.
      */}
      <PopoverContent
        align="end"
        className="w-60 p-0"
        disablePortal
        onWheel={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-border border-b px-3 py-2">
          <span className="text-secondary text-xs-500">Categories</span>
          {isFiltered && (
            <button
              type="button"
              className="text-secondary text-xs-400 hover:text-foreground hover:underline"
              onClick={() => onChange([])}
              data-testid="button-clear-category-filter"
            >
              Clear
            </button>
          )}
        </div>
        {/* The cap must be on `viewportClassName`: the `h-full` viewport cannot derive a bound
            from a Root carrying only `max-h`. */}
        <ScrollArea hideScrollbar viewportClassName="max-h-[264px]">
          <div className="p-1">
            {categories.map(({ name, count }) => {
              const selected = value.includes(name)
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggle(name)}
                  aria-pressed={selected}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-hv1"
                  data-testid={`option-category-${toTestId(name)}`}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 flex-none items-center justify-center rounded-[4px] border',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-st1 bg-background',
                    )}
                  >
                    {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-foreground text-s-400">{name}</span>
                  <span className="flex-none text-tertiary text-xs-400">{count}</span>
                </button>
              )
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
